import TextField from "@mui/material/TextField";
import { TextFieldVariants } from "@mui/material";
import { styled } from "@mui/material/styles";
import { forwardRef, useState, useEffect } from "react";

import { InvalidEmailAddressError } from "common/errors";
import EmailAddress from "../email-address";

const InputField = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    marginRight: 0,
    width: '100%'
  },
  marginBottom: theme.spacing(1.5),
  minWidth: theme.spacing(40),
  ":first-of-type": {
    marginRight: theme.spacing(1.5)
  }
}));

type EmailInputProps = {
  idPrefix?: string;
  className?: string;
  variant?: TextFieldVariants;
  disabled?: boolean;
  emailAddress?: EmailAddress;
  onChange?: (emailAddress?: EmailAddress, error?: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
};

const EmailAddressInput = forwardRef(
  function EmailAddressInput(props: EmailInputProps, _ref) {
  const { idPrefix, className, variant, disabled, emailAddress, onChange, onKeyDown } = props;

  const [emailFieldText, setEmailFieldText] = useState<string>(emailAddress?.value ?? '');
  const [emailFieldError, setEmailFieldError] = useState<string>('');

  useEffect(() => {
    setEmailFieldText(emailAddress?.value ?? '');
  }, [emailAddress]);

  function buildEmailAddress(inputText?: string): EmailAddress | undefined {
    try {
      if (!inputText) throw new InvalidEmailAddressError("Email address is required");
      return new EmailAddress(inputText);
    } catch (error) {
      if (error instanceof InvalidEmailAddressError) {
        throw error;
      }
    }
  }

  function handleEmailChanged(emailAddress?: string) {
    let newEmailAddress: EmailAddress | undefined;
    let newErrors: string = '';

    try {
      newEmailAddress = buildEmailAddress(emailAddress);
      setEmailFieldError('');
    } catch (error) {
      if (!(error instanceof InvalidEmailAddressError)) return;
      newErrors = error.message;
      setEmailFieldError(newErrors);
    } finally {
      onChange?.(newEmailAddress, newErrors);
    }
  }

  return (
    <InputField
      id={idPrefix ? idPrefix + "-email-address-input" : "email-address-input"}
      className={className}
      label="Email Address"
      onChange={(event) => {
        setEmailFieldText(event.target.value);
        handleEmailChanged(event.target.value);
      }}
      required
      disabled={disabled}
      value={emailFieldText}
      error={!!emailFieldError}
      helperText={emailFieldError}
      variant={variant}
      autoComplete="account-email"
      type="email"
      onKeyDown={onKeyDown}
    />
  );
});

export default EmailAddressInput;
